import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { ForwardedRef, forwardRef, ReactElement, useEffect, useRef, useState } from 'react';

import { useTablet } from '@/lib/hooks/useTablet';
import { breakpoints } from '@theme/lib';
import { useTheme } from 'styled-components';
import StyledButton from '../../Button';
import CampaignLogo from '../../CampaignLogo';
import { StyledCampaignHeader, StyledHeader } from './styles';

export type CampaignHeaderProps = {
  cta?: {
    url: string;
    title: string;
  };
};

type HeroCampaignHeaderProps = { isTablet: boolean; name: string };

type ScrolledPastHeroCampaignHeaderProps = {
  isTablet: boolean;
  name: string;
  cta?: {
    url: string;
    title: string;
  };
  invisible?: boolean;
};

function HeroCampaignHeader({ isTablet, name }: HeroCampaignHeaderProps): ReactElement<any> {
  return (
    <StyledHeader className={isTablet ? 'mobile' : 'desktop'} isTablet={isTablet}>
      <div className="header-gradient" />
      <div className="header-wrapper">
        <Link href="/" aria-label={`${name}`} className="header-logo-wrapper" target="_blank">
            <CampaignLogo isScrolledPastHero={false} />
        </Link>
      </div>
    </StyledHeader>
  );
}

const ScrolledPastHeroCampaignHeader = forwardRef(
  (
    { isTablet, name, cta, invisible = false }: ScrolledPastHeroCampaignHeaderProps,
    ref: ForwardedRef<HTMLDivElement | null>
  ): ReactElement<any> => {
    // Invisible ScrolledPastHeroCampaignHeader is used to measure the height of the Header
    // It's invisible to visual and screen reader users
    return (
      <StyledCampaignHeader
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        // Direct children of AnimatePresence must each have a unique key prop so AnimatePresence can track their presence in the tree.
        key="campaign-header"
        transition={{ duration: 0.2 }}
        className={`preload ${isTablet ? 'mobile' : 'desktop'}`}
        isTablet={isTablet}
        // Hide the component from screen readers if it's invisible
        aria-hidden={invisible ? 'true' : 'false'}
        invisible={invisible}
        // If the component's invisible, render it as a div, so we don't have duplicate headers in HTML
        as={invisible ? motion.div : undefined}
      >
        <div className="header-gradient" ref={ref} />
        <div className="header-wrapper">
          <Link href="/" aria-label={`${name}`} className="header-logo-wrapper" target="_blank">
            <CampaignLogo isScrolledPastHero />
          </Link>
          {cta && (
            <StyledButton href={cta.url} className="header-button" primary={true}>
              {cta.title}
            </StyledButton>
          )}
        </div>
      </StyledCampaignHeader>
    );
  }
);
ScrolledPastHeroCampaignHeader.displayName = 'ScrolledPastHeroCampaignHeader';

export default function CampaignHeader({ cta }: CampaignHeaderProps): ReactElement<any> {
  const [isScrolledPastHero, setIsScrolledPastHero] = useState(false);

  const isTablet = useTablet(Number(breakpoints.mdLogo));
  const { name } = useTheme();
  const invisibleHeaderRef = useRef<HTMLDivElement | null>(null);

  /** Avoid flickering effect
   * of the hover animation
   * on the menu elements
   * on first load */
  useEffect(() => {
    const preload = setTimeout(function () {
      document.getElementsByTagName('header')[0].classList.remove('preload');
    }, 500);

    return () => clearTimeout(preload);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const heroWrapper = document.querySelector('.hero__wrapper') as HTMLElement;

      // Check if the user has scrolled past the "hero__wrapper" element
      if (heroWrapper && invisibleHeaderRef.current) {
        // headerOffset is the height of the ScrolledPastHeroHeader plus some padding
        const headerOffset = invisibleHeaderRef.current.offsetHeight + 32;
        // Minus headerOffset so that the header will fade in slightly above the main content
        const scrolledPastHero =
          window.scrollY > heroWrapper.offsetTop + heroWrapper.offsetHeight - headerOffset;
        setIsScrolledPastHero(scrolledPastHero);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {isScrolledPastHero ? (
          <ScrolledPastHeroCampaignHeader
            isTablet={isTablet}
            name={name}
            cta={cta}
            key="scrolled-past-hero-header"
          />
        ) : (
          <HeroCampaignHeader isTablet={isTablet} name={name} key="hero-header" />
        )}
      </AnimatePresence>
      {/* Render an invisible Header so we can measure its height */}
      <ScrolledPastHeroCampaignHeader
        isTablet={isTablet}
        name={name}
        cta={cta}
        invisible
        ref={invisibleHeaderRef}
      />
    </>
  );
}
